import React, {useState, useEffect, forwardRef} from 'react';
//import ReCAPTCHA from 'react-google-recaptcha';
import { useUserContext } from "../../contexts";
import { providerLoginFn } from '../../lib/api';
import { useGoogleLogin } from '@react-oauth/google';
import { appleAuthHelpers } from 'react-apple-signin-auth';
import useFeature from '../../hooks/useFeature';
import config from '../../lib/config';
import { setSessionAuthorizationToken } from '../../lib/axiosInstance';
import { setPersistedValue } from '../../lib/utils';
import './Signup.scss';

const EyeIcon = ({open = false}) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        {open
            ? (
                <>
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                    <circle cx="12" cy="12" r="3" />
                </>
            )
            : (
                <>
                    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-10-7-10-7a18.5 18.5 0 0 1 5.07-5.94" />
                    <path d="M9.9 4.24A10.36 10.36 0 0 1 12 4c7 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19" />
                    <path d="M14.12 14.12A3 3 0 0 1 9.88 9.88" />
                    <line x1="2" y1="2" x2="22" y2="22" />
                </>
            )
        }
    </svg>
);

const AppleIcon = () => (
    <svg className="social-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" />
    </svg>
);

const Signup = forwardRef(({closeCallback, signupSuccessCallback, defaultEmail, planId=false, provider=false, notClosable=false, visible=true, loginInsteadCallback=null}, ref) => {

    //const reCaptchaRef = useRef(null);

    //Custom Hooks
    const {userToken, setUserToken, setRefreshToken} = useUserContext();

    const [email, setEmail] = useState(defaultEmail ? defaultEmail : '');
    const [password, setPassword] = useState('');
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [errorMsg, setErrorMsg] = useState(false);
    const [socialLoading, setSocialLoading] = useState(false);
    const [emailSignupLoading, setEmailSignupLoading] = useState(false);

    //const [recaptchaToken, setRecaptchaToken] = useState(false);

    const shouldShowPasswordField = String(email ?? '').trim().length > 0;
    const canContinue = (
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
        && (password ?? '').length >= 8
    );

    const completeSignupAuthentication = ({ access_token, refresh_token }) => {
        setSessionAuthorizationToken(access_token);
        setPersistedValue(config.sessionTokenLocalStorageKey, config.sessionTokenCookieKey, access_token);
        setPersistedValue(config.refreshTokenLocalStorageKey, config.refreshTokenCookieKey, refresh_token);
        setRefreshToken(refresh_token);
        setUserToken(access_token);
    };

    const handlePasswordValidate = () => {
        if((password ?? '').length < 8){
            setErrorMsg("Password must be at least 8 characters.");
            return false;
        }
        return true;
    };

    const handleEmailSignup = async () => {
        if(emailSignupLoading || socialLoading){
            return false;
        }

        /*
        let localRecaptchaToken = false;
        try{
            localRecaptchaToken = await reCaptchaRef.current.executeAsync();


            console.log("RECAPTCHA token", localRecaptchaToken);
        }catch(e){
            console.log("RECAPTCHA ERROR", e);
            return;
        }
        */
        setErrorMsg(false);

        if(!handleEmailValidate()){
            return false;
        }
        if(!handlePasswordValidate()){
            return false;
        }

        const data = {
            username: email,
            email,
            password,
            //'g-recaptcha-response': localRecaptchaToken,
        };
        if (planId && provider){
            data.provider = provider;
            data.plan_id = planId;
        }

        setEmailSignupLoading(true);

        try{
            completeSignupAuthentication(await providerLoginFn('email', data));
            //signupSuccessCallback();
        } catch(res) {
            console.log("error signup", res);
            setErrorMsg(res?.message || 'Signup failed. Please try again.');
        } finally {
            setEmailSignupLoading(false);
        }

        //reCaptchaRef.current.reset();
        return true;
    };

    const googleLogin = useGoogleLogin({
        onSuccess: tokenResponse => {
            providerLoginFn('google', tokenResponse)
            .then(completeSignupAuthentication)
            .catch(() => {
                setErrorMsg('Login failed. Please try again.');
            })
            .finally(() => {
                setSocialLoading(false);
            });
        },
        onError: () => {
            setErrorMsg('Login failed. Please try again.');
            setSocialLoading(false);
        },
    });

    const appleLoginEnabled = useFeature('appleLogin');

    const handleAppleLogin = async () => {
        if(socialLoading || emailSignupLoading){
            return;
        }

        setErrorMsg(false);
        setSocialLoading(true);
        try {
            const response = await appleAuthHelpers.signIn({
                authOptions: {
                    clientId: config.appleClientId,
                    scope: 'email name',
                    redirectURI: window.location.origin + '/login',
                    usePopup: true,
                },
                onError: (error) => {
                    if (error?.error === 'popup_closed_by_user') return;
                    console.error(error?.error ?? 'Apple OAuth Error');
                },
            });
            if (response) {
                providerLoginFn('apple', {
                    access_token: response.authorization.id_token,
                    email: response.user?.email,
                    first_name: response.user?.name?.firstName,
                    last_name: response.user?.name?.lastName,
                })
                .then(completeSignupAuthentication)
                .catch(() => {
                    setErrorMsg('Login failed. Please try again.');
                })
                .finally(() => {
                    setSocialLoading(false);
                });
            } else {
                setSocialLoading(false);
            }
        } catch (error) {
            setSocialLoading(false);
            if (error?.error === 'popup_closed_by_user') return;
            setErrorMsg('Login failed. Please try again.');
        }
    };

    const handleEmailChange = (event) => {
        //({target}) => setEmail(target.value);
        let value = event.target.value;

        // Filter unsupported characters.
        value = value.replace(/[^a-zA-Z0-9@._+-]/g, '');

        // Normalize email casing.
        value = value.toLowerCase();

        setEmail(value ?? '');
    };

    const handleEmailValidate = () => {

        if(! /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)){
            setErrorMsg("The email field must be a valid email address.");
            return false;
        }
        setErrorMsg(false);
        return true;
    };

    const handleFormSubmit = (event) => {
        event.preventDefault();
        handleEmailSignup();
    };

    useEffect(() => {
        if(!userToken){
            return;
        }
        signupSuccessCallback(userToken);
    }, [signupSuccessCallback, userToken]);

    return (
        <div className={`signup ${visible ? 'visible' : ''}`} ref={ref}>
            <div className="signup-grid-overlay" aria-hidden="true" />

            {!defaultEmail && !notClosable && (
                <button type="button" className="close-btn" onClick={() => closeCallback()}>
                    +
                </button>
            )}

            <form className="form" onClick={(event) => event.stopPropagation()} onSubmit={handleFormSubmit}>
                <div className="signup-card">
                    <div className="title">{defaultEmail ? 'Register with email' : 'Claim your account'}</div>

                    {!defaultEmail && (
                        <>
                            <div className="social-actions">
                                <button
                                    type="button"
                                    className={`social-btn social-btn-google${socialLoading ? ' social-loading' : ''}`}
                                    disabled={socialLoading || emailSignupLoading}
                                    onClick={() => {
                                        setErrorMsg(false);
                                        setSocialLoading(true);
                                        googleLogin();
                                    }}
                                >
                                    <img className="social-icon" src="/images/icon.google.svg" alt="google icon" />
                                    <span>Continue with Google</span>
                                </button>

                                {appleLoginEnabled && (
                                    <button
                                        type="button"
                                        className={`social-btn social-btn-apple${socialLoading ? ' social-loading' : ''}`}
                                        disabled={socialLoading || emailSignupLoading}
                                        onClick={handleAppleLogin}
                                    >
                                        <AppleIcon />
                                        <span>Continue with Apple</span>
                                    </button>
                                )}
                            </div>

                            <div className="divider">
                                <span className="line" />
                                <span className="label">OR USE EMAIL</span>
                                <span className="line" />
                            </div>
                        </>
                    )}

                    <label className="field-block" htmlFor="signup-email">
                        <span className="field-label">Email</span>
                        <div className="field-control">
                            <input
                                id="signup-email"
                                type="email"
                                placeholder="Email"
                                disabled={!!defaultEmail}
                                onChange={handleEmailChange}
                                onBlur={handleEmailValidate}
                                value={email}
                            />
                        </div>
                    </label>

                    <div className={`password-reveal ${shouldShowPasswordField ? 'visible' : ''}`}>
                        <label className="field-block" htmlFor="signup-password">
                            <span className="field-label">Password</span>
                            <div className="field-control">
                                <input
                                    id="signup-password"
                                    type={passwordVisible ? 'text' : 'password'}
                                    placeholder="Min. 8 characters"
                                    onChange={({target}) => setPassword(target.value ?? '')}
                                    value={password}
                                />
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setPasswordVisible(!passwordVisible)}
                                    aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                                >
                                    <EyeIcon open={passwordVisible} />
                                </button>
                            </div>
                        </label>
                    </div>

                    {errorMsg && <div className="error-msg">{errorMsg}</div>}

                    <button
                        type="submit"
                        className={`cta-btn${canContinue ? ' ready' : ''}${emailSignupLoading ? ' loading' : ''}`}
                        disabled={emailSignupLoading || socialLoading}
                    >
                        {emailSignupLoading ? 'Signing up...' : 'Continue'}
                    </button>

                    {!defaultEmail && !!planId && !!provider && typeof loginInsteadCallback === 'function' && (
                        <div className="login-instead">
                            <span>Already have an account?</span>
                            <button type="button" onClick={loginInsteadCallback}>Log in</button>
                        </div>
                    )}

                    {defaultEmail && (
                        <button type="button" className="go-back" onClick={() => closeCallback()}>
                            Go Back
                        </button>
                    )}

                    {!defaultEmail && (
                        <div className="go-back-inline">
                            <button type="button" onClick={() => closeCallback()}>Go Back</button>
                        </div>
                    )}

                </div>
            </form>
            {/*
            <ReCAPTCHA
                sitekey={config.recaptchaSiteKey}
                size="invisible"
                ref={reCaptchaRef}
            />
            */}
        </div>
    );
});

export default Signup;
